import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider, getStrings, LANGS, setActiveLang } from "./i18n";
import { AuthProvider, ProtectedRoute } from "./auth/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import Home from "./pages/Home";
import About from "./pages/About";
import Standards from "./pages/Standards";
import StandardDetail from "./pages/StandardDetail";
import Services from "./pages/Services";
import Certification from "./pages/Certification";
import ERI from "./pages/ERI";
import Insights from "./pages/Insights";
import EriAssessment from "./pages/EriAssessment";
import Article from "./pages/Article";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import Faq from "./pages/Faq";
import Verify from "./pages/Verify";
import Legal from "./pages/Legal";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import ChangePassword from "./pages/auth/ChangePassword";
import PortalLayout from "./pages/portal/PortalLayout";
import Dashboard from "./pages/portal/Dashboard";
import Assessments from "./pages/portal/Assessments";
import AssessmentDetail from "./pages/portal/AssessmentDetail";
import Certificates from "./pages/portal/Certificates";
import RequestService from "./pages/portal/RequestService";
import Account from "./pages/portal/Account";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminEri from "./pages/admin/AdminEri";
import AdminEriDetail from "./pages/admin/AdminEriDetail";
import AdminMail from "./pages/admin/AdminMail";
import AdminClients from "./pages/admin/AdminClients";
import AdminClientDetail from "./pages/admin/AdminClientDetail";
import AdminAssessmentDetail from "./pages/admin/AdminAssessmentDetail";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LangShell() {
  const { lang } = useParams();
  if (!LANGS.includes(lang)) {
    return <Navigate to="/en" replace />;
  }
  setActiveLang(lang);
  return (
    <LanguageProvider value={{ lang, t: getStrings(lang) }}>
      <Outlet key={lang} />
    </LanguageProvider>
  );
}

function MarketingLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-esaia-canvas">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Navigate to="/en" replace />} />
          <Route path="/:lang" element={<LangShell />}>
            <Route element={<MarketingLayout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="standards" element={<Standards />} />
              <Route path="standards/:code" element={<StandardDetail />} />
              <Route path="services" element={<Services />} />
              <Route path="certification" element={<Certification />} />
              <Route path="eri" element={<ERI />} />
              <Route path="eri/assessment" element={<EriAssessment />} />
              <Route path="insights" element={<Insights />} />
              <Route path="insights/:slug" element={<Article />} />
              <Route path="contact" element={<Contact />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="faq" element={<Faq />} />
              <Route path="verify" element={<Verify />} />
              <Route path="legal/:doc" element={<Legal />} />
              <Route path="*" element={<Navigate to="/en" replace />} />
            </Route>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route
              path="portal"
              element={
                <ProtectedRoute role="client">
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="assessments" element={<Assessments />} />
              <Route path="assessments/:id" element={<AssessmentDetail />} />
              <Route path="certificates" element={<Certificates />} />
              <Route path="request-service" element={<RequestService />} />
              <Route path="account" element={<Account />} />
            </Route>
            <Route
              path="admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="eri" replace />} />
              <Route path="eri" element={<AdminEri />} />
              <Route path="eri/:id" element={<AdminEriDetail />} />
              <Route path="mail" element={<AdminMail />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:id" element={<AdminClientDetail />} />
              <Route path="assessments/:id" element={<AdminAssessmentDetail />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
